import React, { useEffect, Fragment, useContext } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import { Typography, Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { withRouter, RouteComponentProps } from "react-router";

import { Icon } from "../../components/icons/Icon";
import routes from "../../constants/routes";
import { PageHeader, PageRoot, TextField, Button } from "../../components";

import DetailCard from "../../components/details/DetailCard";
import { User, Role } from "../../constants/interfaces";
import { userActions } from "../../reducers/user";
import { INDIVIDUAL_DETAILS } from "../../constants/messages";
import hasUserRole from "../../utils/hasUserRole";
import { RxForm, FormValues, FormSection } from "../../components/Form";
import FormTextField from "../../components/Fields/FormTextField";
import CheckboxField from "../../components/Fields/CheckboxField";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import { OnUnSaveChange } from "../../components/Form/OnUnSaveChange";
import { ErrorMessage } from "../../components/Messages";
import { UIMessageContext } from "../../components/UIMessage";
import { emailValidator } from "./validator";

const useStyles = makeStyles(theme => ({
  form: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column"
  },
  formCard: {
    padding: theme.spacing(4),
    maxWidth: theme.breakpoints.values.md
  },
  header: {
    alignItems: "center",
    display: "flex"
  },
  icon: {
    color: theme.palette.primary.light,
    marginRight: theme.spacing(2)
  },
  contentWrapper: {
    display: "flex",
    flexFlow: "row wrap",
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(4)
  },
  asideWrapper: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  error: {
    marginBottom: theme.spacing(2)
  },
  buttonWrapper: {
    display: "flex",
    "&>button": {
      marginRight: "1.2em"
    }
  },
  fields: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  row: {
    display: "flex",
    "& > div": {
      flex: 1
    },
    "& > :first-child": {
      marginRight: 10
    }
  }
}));

type Props = RouteComponentProps & {
  classes?: any;
  error?: Error;
  loading: boolean;
  getUserDetails: () => Promise<User>;
  updateUserDetails: (user: User, callback?: () => void) => Promise<void>;
  user: User;
};

const MyDetails: React.FC<Props> = props => {
  const classes = useStyles();
  const { showMessage } = useContext(UIMessageContext);

  useEffect(() => {
    props.getUserDetails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { user } = props;

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.HOME.label,
            route: routes.HOME.url
          }
        ]}
        label={routes.MY_DETAILS.label}
      />

      <Card className={classes.formCard}>
        <div className={classes.header}>
          <Icon className={classes.icon} fontSize="large" name="person" />
          <Typography color="textSecondary" component="h2" variant="h5">
            Personal Information
          </Typography>
        </div>

        <RxForm
          className={classes.form}
          confirmMessage={INDIVIDUAL_DETAILS}
          onSubmit={value => {
            const {
              emailAddress,
              postalAddress,
              postalAndStreetAddessSame,
              streetAddress,
              phoneNumber,
              mobileNumber
            } = value;
            const user: User = {
              ...props.user,
              addressDetails: {
                postalAddress: null,
                postalAddressSuburbOrRDNumber: null,
                postalAddressTownOrCity: null,
                postalAddressPostCode: null,
                postalAddressCountry: null,
                postalAddressOtherDetails: null,
                ...streetAddress,
                ...postalAddress,
                postalAndStreetAddessSame
              },
              phoneNumber,
              mobileNumber,
              emailAddress
            };
            return props.updateUserDetails(user, () => {
              showMessage("Your details are updated", "success");
            });
          }}
        >
          <OnUnSaveChange />
          <div className={classes.contentWrapper}>
            <div className={classes.fields}>
              <TextField
                id="givenName"
                label="Given Name"
                disabled
                value={user.givenName || ""}
              />

              <TextField
                id="middleName"
                label="Middle Name(s)"
                disabled
                value={user.middleName || ""}
              />

              <TextField
                id="surname"
                label="Surname"
                disabled
                value={user.surname || ""}
              />
              <div className={classes.row}>
                <FormTextField
                  id="phoneNumber"
                  name="phoneNumber"
                  label="Phone Number"
                  defaultValue={_.get(user, "phoneNumber")}
                />

                <FormTextField
                  id="mobileNumber"
                  name="mobileNumber"
                  label="Mobile Number"
                  defaultValue={_.get(user, "mobileNumber")}
                />
              </div>

              <FormTextField
                required
                id="emailAddress"
                name="emailAddress"
                label="Email"
                inputProps={emailValidator}
                defaultValue={_.get(user, "emailAddress")}
              />

              <FormSection name="streetAddress">
                <FormTextField
                  id="streetAddressOtherDetails"
                  name="streetAddressOtherDetails"
                  label="Street Address Other detail"
                  defaultValue={_.get(
                    props.user,
                    "addressDetails.streetAddressOtherDetails"
                  )}
                />

                <FormTextField
                  required
                  id="streetAddress"
                  name="streetAddress"
                  label="Street Address"
                  defaultValue={_.get(
                    props.user,
                    "addressDetails.streetAddress"
                  )}
                />
                <div className={classes.row}>
                  <FormTextField
                    id="streetAddressSuburbOrRDNumber"
                    name="streetAddressSuburbOrRDNumber"
                    label="Suburb"
                    defaultValue={_.get(
                      props.user,
                      "addressDetails.streetAddressSuburbOrRDNumber"
                    )}
                  />

                  <FormTextField
                    required
                    id="streetAddressTownOrCity"
                    name="streetAddressTownOrCity"
                    label="City"
                    defaultValue={_.get(
                      props.user,
                      "addressDetails.streetAddressTownOrCity"
                    )}
                  />
                </div>
                <div className={classes.row}>
                  <FormTextField
                    id="streetAddressCountry"
                    name="streetAddressCountry"
                    label="Country"
                    defaultValue={_.get(
                      props.user,
                      "addressDetails.streetAddressCountry"
                    )}
                  />

                  <FormTextField
                    required
                    id="streetAddressPostCode"
                    name="streetAddressPostCode"
                    label="Post Code"
                    defaultValue={_.get(
                      props.user,
                      "addressDetails.streetAddressPostCode"
                    )}
                  />
                </div>
              </FormSection>

              <CheckboxField
                name="postalAndStreetAddessSame"
                label="Postal address is the same as street address"
                defaultValue={_.get(
                  props.user,
                  "addressDetails.postalAndStreetAddessSame"
                )}
              />

              <FormValues>
                {({ formValues }) => {
                  const { postalAndStreetAddessSame } = formValues;

                  if (postalAndStreetAddessSame) return null;

                  return (
                    <FormSection name="postalAddress">
                      <FormTextField
                        id="postalAddressOtherDetails"
                        name="postalAddressOtherDetails"
                        label="Postal Address Other detail"
                        destroyValueOnUnmount
                        defaultValue={_.get(
                          props.user,
                          "addressDetails.postalAddressOtherDetails"
                        )}
                      />

                      <FormTextField
                        required
                        destroyValueOnUnmount
                        id="postalAddress"
                        name="postalAddress"
                        label="Postal Address"
                        defaultValue={_.get(
                          props.user,
                          "addressDetails.postalAddress"
                        )}
                      />
                      <div className={classes.row}>
                        <FormTextField
                          destroyValueOnUnmount
                          id="postalAddressSuburbOrRDNumber"
                          name="postalAddressSuburbOrRDNumber"
                          label="Suburb"
                          defaultValue={_.get(
                            props.user,
                            "addressDetails.postalAddressSuburbOrRDNumber"
                          )}
                        />

                        <FormTextField
                          required
                          destroyValueOnUnmount
                          id="postalAddressTownOrCity"
                          name="postalAddressTownOrCity"
                          label="City"
                          defaultValue={_.get(
                            props.user,
                            "addressDetails.postalAddressTownOrCity"
                          )}
                        />
                      </div>
                      <div className={classes.row}>
                        <FormTextField
                          destroyValueOnUnmount
                          id="postalAddressCountry"
                          name="postalAddressCountry"
                          label="Country"
                          defaultValue={_.get(
                            props.user,
                            "addressDetails.postalAddressCountry"
                          )}
                        />

                        <FormTextField
                          required
                          destroyValueOnUnmount
                          id="postalAddressPostCode"
                          name="postalAddressPostCode"
                          label="Post Code"
                          defaultValue={_.get(
                            props.user,
                            "addressDetails.postalAddressPostCode"
                          )}
                        />
                      </div>
                    </FormSection>
                  );
                }}
              </FormValues>
            </div>

            <aside className={classes.asideWrapper}>
              <Fragment>
                {hasUserRole(user, Role.LEGAL_ENTITIES) &&
                  Boolean(user.legalEntitiesCount) && (
                    <DetailCard
                      id="legalEntities"
                      onView={() =>
                        props.history.push(routes.MY_LEGAL_ENTITIES.url)
                      }
                      title="My Legal Entities"
                      listItems={[
                        {
                          icon: "account_balance",
                          label: `${user.legalEntitiesCount} ${
                            user.legalEntitiesCount === 1
                              ? "legal entity"
                              : "legal entities"
                          }`
                        }
                      ]}
                    />
                  )}
                {hasUserRole(user, Role.PREMISES) &&
                  Boolean(user.premisesCount) && (
                    <DetailCard
                      id="premises"
                      onView={() => props.history.push(routes.MY_PREMISES.url)}
                      title="My Premises"
                      listItems={[
                        {
                          icon: "business",
                          label: `${user.premisesCount} premises`
                        }
                      ]}
                    />
                  )}
              </Fragment>
            </aside>
          </div>

          {props.error && (
            <ErrorMessage className={classes.error} error={props.error} />
          )}
          <div className={classes.buttonWrapper}>
            <SubmitButton label="Update" />
            <ResetButton label="Reset" />
            <Button
              onClick={() => {
                props.history.push(`${routes.HOME.url}`);
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapDispatchToProps = {
  getUserDetails: userActions.getUserDetails,
  updateUserDetails: userActions.updateUserDetails
};

const mapStateToProps = ({ user }: any) => {
  return {
    error: user.error,
    loading: user.loading,
    user: user.user
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MyDetails));
